/**
 * The concierge — agent mode.
 *
 * The scripted decision tree in `Concierge.tsx` is still the fallback and still
 * ships; this renders instead of it when the deployment has a model key
 * (`conciergeAgent.agentAvailable`). Same corner, same auto-yield manners, same
 * refusal to appear on routes that own the screen.
 *
 * WHAT IS DIFFERENT, AND WHY IT IS BUILT THIS WAY
 * ----------------------------------------------
 * · **It streams.** The reply is a database row the server patches as tokens
 *   arrive, so this component does nothing clever — it subscribes once and
 *   re-renders. No streaming transport, no polling, and a half-written answer
 *   survives a page reload because it is committed rather than held in state.
 *
 * · **The work is visible.** Tool calls render as chips the moment they start
 *   and settle in place when they finish. The brief was explicit that the
 *   customer should be able to see the agent working; a spinner that hides
 *   which tool ran is the thing it asked us not to build.
 *
 * · **It is glass.** See `.iv-glass` in `index.css` — blur plus saturation plus
 *   a lit rim, with real refraction layered on top only where the browser can
 *   actually do it.
 *
 * · **No account needed.** Anonymous visitors get a smaller ceiling rather than
 *   a sign-in wall, because the person most worth helping here is the one who
 *   has not signed up yet. The limit is enforced server-side; this only reports
 *   what the server said.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { useLocation } from 'wouter';
import { MessageCircle, X, GripVertical, Send, ArrowUpRight, AlertCircle } from 'lucide-react';
import { api } from '../../../convex/_generated/api';
import { capture, EVENTS } from '@/lib/posthog';
import { warn } from '@/lib/logger';
import { getCollectorSessionId } from '@/lib/collectorSession';
import { applyGlassRefractionClass } from '@/lib/glassRefraction';
import { useJourneyPause, hasBeenNudged } from '@/hooks/use-journey-pause';
import { GREETING } from '@/content/concierge';
import {
  DEFAULT_PANEL,
  DRAG_THRESHOLD_PX,
  clampSize,
  clampToViewport,
  clearDragPos,
  readDragPos,
  readPanelSize,
  resizeFrom,
  EDGE_CURSOR,
  type ResizeEdge,
  writeDragPos,
  writePanelSize,
  type Point,
  type Size,
} from '@/lib/conciergePanel';
import { MAX_MESSAGE_LENGTH } from '../../../convex/lib/conciergeAgent';
import { GlassRefractionFilter } from './GlassRefractionFilter';
import { HumanCheck } from './HumanCheck';
import { onConciergeOpen } from '@/lib/conciergeBus';
import { INTERVIEW_MODE } from '../../../convex/lib/collectorInterview';

type Corner = 'br' | 'bl';

/**
 * The grab strips around the border.
 *
 * 10px edges and 16px corners: wide enough to hit without aiming, narrow
 * enough that they never cover content. Corners come AFTER edges in this list
 * so they paint on top and win the overlap — a corner drag should resize both
 * axes, not whichever edge happened to render last.
 */
const RESIZE_ZONES: ReadonlyArray<{ edge: ResizeEdge; className: string }> = [
  { edge: 'n', className: 'left-0 right-0 top-0 h-[10px]' },
  { edge: 's', className: 'bottom-0 left-0 right-0 h-[10px]' },
  { edge: 'w', className: 'bottom-0 left-0 top-0 w-[10px]' },
  { edge: 'e', className: 'bottom-0 right-0 top-0 w-[10px]' },
  { edge: 'nw', className: 'left-0 top-0 h-4 w-4' },
  { edge: 'ne', className: 'right-0 top-0 h-4 w-4' },
  { edge: 'sw', className: 'bottom-0 left-0 h-4 w-4' },
  { edge: 'se', className: 'bottom-0 right-0 h-4 w-4' },
];

const POSITION_KEY = 'iv_concierge_corner';

/** Routes where a floating panel would compete with the page's own job. */
// `/promo/insert` mounts the PAGE variant itself; the corner widget stays out of its way.
const SUPPRESSED_PREFIXES = ['/admin', '/checkout', '/sign-in', '/create-account', '/promo/insert'];

function readCorner(): Corner {
  try {
    return window.localStorage.getItem(POSITION_KEY) === 'bl' ? 'bl' : 'br';
  } catch {
    return 'br';
  }
}

export interface ConciergeAgentProps {
  /**
   * `widget` (default) is the draggable corner panel. `page` fills whatever
   * box it is mounted in — the insert-card route, where a phone scanner gets
   * the whole screen — and opens straight into the interview.
   */
  variant?: 'widget' | 'page';
  /** Page variant only: the printed card this visit came through. */
  campaignKey?: string;
}

export function ConciergeAgent({ variant = 'widget', campaignKey }: ConciergeAgentProps = {}): JSX.Element | null {
  const isPage = variant === 'page';
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(isPage);
  const [corner, setCorner] = useState<Corner>('br');
  const [isYielding, setIsYielding] = useState(false);
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [nudgeVisible, setNudgeVisible] = useState(false);
  /* Free position and size. Null position = still anchored bottom-right. */
  const [dragPos, setDragPos] = useState<Point | null>(null);
  const [panelSize, setPanelSize] = useState<Size>(DEFAULT_PANEL);
  /* Refs mirror the state so the pointer handlers, which are bound once, can
     read current values without being re-created on every move. */
  const dragPosRef = useRef<Point | null>(null);
  const panelSizeRef = useRef<Size>(DEFAULT_PANEL);
  const toggleCornerRef = useRef<() => void>(() => undefined);

  const sessionId = useMemo(() => getCollectorSessionId(), []);
  const thread = useQuery(api.conciergeAgent.thread, { sessionId });
  const profile = useQuery(api.collector.getMyProfile, { sessionId });
  const send = useMutation(api.conciergeAgent.send);
  const greet = useMutation(api.conciergeAgent.greet);
  const startInterview = useMutation(api.conciergeAgent.startInterview);
  const giveConsent = useMutation(api.conciergeAgent.giveConsent);
  const verifyHuman = useAction(api.conciergeAgent.verifyHuman);
  const [consentBusy, setConsentBusy] = useState(false);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const yieldTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const messages = thread?.messages ?? [];
  // Server-decided: false for a signed-in visitor, false where Turnstile is not
  // configured, and false once this thread has passed within the last 12h.
  const needsHumanCheck = thread?.needsHumanCheck ?? false;

  /*
   * The backend wants a human check but this BUILD has no Turnstile site key,
   * so the challenge cannot render and the visitor can never pass it.
   *
   * Without this the composer would sit disabled forever with no explanation —
   * a dead widget that looks like a bug. `VITE_` variables are inlined at build
   * time, so a deployed bundle can genuinely lack the key while the Convex
   * secret is set; the two are configured in different places and can drift.
   */
  const canRunHumanCheck = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);
  const humanCheckUnavailable = needsHumanCheck && !canRunHumanCheck;
  const isWorking = thread?.status === 'thinking' || thread?.status === 'replying';
  const hasConversation = messages.length > 0;
  const isInterview = thread?.mode === INTERVIEW_MODE;
  const pendingConsentEmail = thread?.interview?.pendingConsentEmail ?? null;
  const consentText = thread?.interview?.consentText ?? '';
  const interviewCompletedAt = thread?.interview?.completedAt ?? null;

  useEffect(() => {
    setCorner(readCorner());
    applyGlassRefractionClass();
    setDragPos(readDragPos());
    setPanelSize(clampSize(readPanelSize() ?? DEFAULT_PANEL));
  }, []);

  /*
   * Re-clamp on every viewport change. A position and size that were fine on a
   * desktop window put the close button off-screen on a phone, and a panel you
   * cannot reach is worse than one that never moved.
   */
  useEffect(() => {
    const onResize = (): void => {
      setPanelSize((s) => clampSize(s));
      setDragPos((p) => {
        const el = panelRef.current;
        return p && el ? clampToViewport(p, el.offsetWidth, el.offsetHeight) : p;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /**
   * Auto-yield: fade and shrink while the page scrolls, come back a beat after.
   * Carried over from the scripted concierge deliberately — it is the single
   * manner that stops a floating panel feeling like an advert.
   */
  useEffect(() => {
    if (isOpen) {
      setIsYielding(false);
      return;
    }
    const onScroll = (): void => {
      setIsYielding(true);
      if (yieldTimer.current) {
        clearTimeout(yieldTimer.current);
      }
      yieldTimer.current = setTimeout(() => setIsYielding(false), 700);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (yieldTimer.current) {
        clearTimeout(yieldTimer.current);
      }
    };
  }, [isOpen]);

  // Follow the stream, and land at the bottom on OPEN. `messages` changes on
  // every token, which keeps the newest line in view while it is written;
  // but a panel opened from the homepage button mounts with the whole
  // conversation already loaded, so nothing changed and it sat at the top
  // (Matthew, 2026-09-06). The frame wait lets the panel paint first.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      const el = scrollRef.current;
      el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages, isOpen]);

  useEffect(() => {
    dragPosRef.current = dragPos;
  }, [dragPos]);
  useEffect(() => {
    panelSizeRef.current = panelSize;
  }, [panelSize]);

  const suppressed = SUPPRESSED_PREFIXES.some((prefix) => location.startsWith(prefix));

  /**
   * The proactive nudge.
   *
   * Only for someone who has not signed in and is not already talking to us —
   * the brief's "first-time visitors or visitors with no account". `profile` is
   * undefined while loading, so the check waits rather than firing at a
   * returning collector on a slow connection.
   */
  const nudgeEligible =
    !suppressed &&
    !isOpen &&
    thread !== undefined &&
    profile !== undefined &&
    !hasConversation &&
    !hasBeenNudged();

  const pause = useJourneyPause({ enabled: nudgeEligible });

  useEffect(() => {
    if (!pause || !nudgeEligible) {
      return;
    }
    setNudgeVisible(true);
    capture(EVENTS.CONCIERGE_NUDGED, { reason: pause, path: window.location.pathname });
  }, [pause, nudgeEligible]);

  const greetingFor = useCallback((): string => {
    const name = profile?.firstName;
    if (!name) {
      return GREETING.stranger;
    }
    return profile?.archetype
      ? GREETING.knownWithArchetype.replace('{name}', name)
      : GREETING.known.replace('{name}', name);
  }, [profile?.archetype, profile?.firstName]);

  const openPanel = useCallback((): void => {
    setIsOpen(true);
    setNudgeVisible(false);
    capture(EVENTS.CONCIERGE_OPENED, {
      path: window.location.pathname,
      is_known: Boolean(profile?.archetype),
      mode: 'agent',
    });
    if (!hasConversation) {
      // Static opener: instant and free. The model takes over from their reply.
      void greet({ sessionId, page: window.location.pathname, greeting: greetingFor() }).catch(
        (cause: unknown) => {
          warn('Concierge greeting not written', {
            reason: cause instanceof Error ? cause.message : String(cause),
          });
        },
      );
    }
    window.setTimeout(() => inputRef.current?.focus(), 120);
  }, [greet, greetingFor, hasConversation, profile?.archetype, sessionId]);

  /**
   * A page asked us to open — the homepage "Share Your Thoughts" button. In
   * interview mode the server writes the opening line (free, instant) and
   * flips the thread into the interview; a thread already in it just resumes.
   */
  useEffect(
    () =>
      onConciergeOpen((mode) => {
        if (mode !== 'interview') {
          openPanel();
          return;
        }
        setIsOpen(true);
        setNudgeVisible(false);
        setSendError(null);
        capture(EVENTS.CONCIERGE_OPENED, { path: window.location.pathname, mode: 'interview' });
        void startInterview({ sessionId, page: window.location.pathname }).catch((cause: unknown) => {
          setSendError(cause instanceof Error ? cause.message.replace(/^\[.*?\]\s*/, '') : String(cause));
        });
        window.setTimeout(() => inputRef.current?.focus(), 120);
      }),
    [openPanel, sessionId, startInterview],
  );

  // Page variant: there is no button to press, the route IS the invitation.
  // Start (or resume) the interview once, carrying the card's key.
  const pageStarted = useRef(false);
  useEffect(() => {
    if (!isPage || pageStarted.current) {
      return;
    }
    pageStarted.current = true;
    capture(EVENTS.CONVERSATION_STARTED, { source: 'insert-card', campaignKey: campaignKey ?? null });
    void startInterview({ sessionId, page: window.location.pathname, campaignKey }).catch((cause: unknown) => {
      setSendError(cause instanceof Error ? cause.message.replace(/^\[.*?\]\s*/, '') : String(cause));
    });
    window.setTimeout(() => inputRef.current?.focus(), 120);
  }, [campaignKey, isPage, sessionId, startInterview]);

  // The completion is a server fact; report it once when it appears.
  const completionSeen = useRef<number | null>(null);
  useEffect(() => {
    if (!interviewCompletedAt || completionSeen.current === interviewCompletedAt) {
      return;
    }
    completionSeen.current = interviewCompletedAt;
    capture(EVENTS.CONVERSATION_COMPLETED, {});
  }, [interviewCompletedAt]);

  const agree = useCallback(async (): Promise<void> => {
    if (consentBusy || isWorking) {
      return;
    }
    setConsentBusy(true);
    setSendError(null);
    try {
      await giveConsent({ sessionId, page: window.location.pathname });
      capture(EVENTS.CONVERSATION_CONSENT_GIVEN, {});
    } catch (cause) {
      const reason = cause instanceof Error ? cause.message : String(cause);
      setSendError(reason.replace(/^\[.*?\]\s*/, ''));
    } finally {
      setConsentBusy(false);
    }
  }, [consentBusy, giveConsent, isWorking, sessionId]);

  /**
   * The visitor passed the challenge. Verification happens SERVER-side — the
   * token is proof for Convex to check with Cloudflare, not a client assertion
   * we act on — so there is nothing to set here beyond surfacing a failure.
   */
  const handleVerified = useCallback(
    async (token: string): Promise<void> => {
      try {
        const ok = await verifyHuman({ sessionId, token });
        if (!ok) {
          setSendError('That check did not pass. Try once more, or use the contact page.');
        } else {
          setSendError(null);
        }
      } catch (cause) {
        warn('Human check could not be verified', {
          reason: cause instanceof Error ? cause.message : String(cause),
        });
        setSendError('That check did not pass. Try once more, or use the contact page.');
      }
    },
    [sessionId, verifyHuman],
  );

  const submit = useCallback(async (): Promise<void> => {
    const text = draft.trim();
    if (text.length === 0 || isWorking) {
      return;
    }
    setDraft('');
    setSendError(null);
    try {
      await send({ sessionId, text, page: window.location.pathname });
      capture(EVENTS.CONCIERGE_ANSWERED, { mode: 'agent' });
    } catch (cause) {
      // The server owns the limits, so its sentence is the honest one to show.
      const reason = cause instanceof Error ? cause.message : String(cause);
      setSendError(reason.replace(/^\[.*?\]\s*/, ''));
      setDraft(text);
    }
  }, [draft, isWorking, send, sessionId]);

  /**
   * Drag the panel by its grip.
   *
   * Pointer capture rather than window listeners: it keeps working when the
   * pointer leaves the panel mid-drag, and it cannot strand a listener if the
   * component unmounts partway through.
   *
   * A press that never travels DRAG_THRESHOLD_PX is treated as a tap and falls
   * through to the corner toggle, so the control still works for anyone who
   * does not think to drag — including keyboard users, who get the toggle.
   */
  const onHeaderPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      // A press on Reset or Close is a click, not a drag. Checking the target
      // keeps those working without stacking tricks that would also swallow
      // their focus ring.
      if ((event.target as HTMLElement).closest('button')) {
        return;
      }
      const el = panelRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      const startX = event.clientX;
      const startY = event.clientY;
      const offsetX = startX - rect.left;
      const offsetY = startY - rect.top;
      let moved = false;

      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);

      const onMove = (move: PointerEvent): void => {
        const dx = move.clientX - startX;
        const dy = move.clientY - startY;
        if (!moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
          return;
        }
        moved = true;
        setDragPos(
          clampToViewport(
            { x: move.clientX - offsetX, y: move.clientY - offsetY },
            rect.width,
            rect.height,
          ),
        );
      };

      const onUp = (): void => {
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        if (moved) {
          setDragPos((current) => {
            if (current) {
              writeDragPos(current);
            }
            return current;
          });
        } else {
          // A press that never travelled is still the old corner toggle, so
          // the behaviour people already had does not disappear.
          toggleCornerRef.current();
        }
      };

      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
    },
    [],
  );

  /**
   * Resize from any of the eight border zones.
   *
   * Converts an anchored panel to explicit left/top on the FIRST resize. While
   * anchored bottom-right by CSS, the right and bottom edges cannot move, so
   * dragging the east or south edge did nothing at all — only the top-left
   * corner behaved. Pinning the current rect once makes every zone behave the
   * same conventional way.
   */
  const startResize = useCallback(
    (edge: ResizeEdge) =>
      (event: React.PointerEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        const el = panelRef.current;
        if (!el) {
          return;
        }
        const rect = el.getBoundingClientRect();
        const startX = event.clientX;
        const startY = event.clientY;
        const startSize: Size = { width: rect.width, height: rect.height };
        const startOrigin: Point = dragPosRef.current ?? { x: rect.left, y: rect.top };

        // Pin it immediately so the very first pointermove has an origin to
        // work from rather than jumping.
        setDragPos(startOrigin);

        const target = event.currentTarget;
        target.setPointerCapture(event.pointerId);

        const onMove = (move: PointerEvent): void => {
          const next = resizeFrom({
            edge,
            startSize,
            startOrigin,
            dx: move.clientX - startX,
            dy: move.clientY - startY,
          });
          setPanelSize(next.size);
          setDragPos(clampToViewport(next.origin, next.size.width, next.size.height));
        };

        const onUp = (): void => {
          target.removeEventListener('pointermove', onMove);
          target.removeEventListener('pointerup', onUp);
          writePanelSize(panelSizeRef.current);
          const pos = dragPosRef.current;
          if (pos) {
            writeDragPos(pos);
          }
        };

        target.addEventListener('pointermove', onMove);
        target.addEventListener('pointerup', onUp);
      },
    [],
  );

  /** Put it back where it started — the way out of a bad drag or resize. */
  const resetPanel = useCallback((): void => {
    setDragPos(null);
    clearDragPos();
    setPanelSize(clampSize(DEFAULT_PANEL));
    writePanelSize(clampSize(DEFAULT_PANEL));
  }, []);

  const toggleCorner = useCallback((): void => {
    const next: Corner = corner === 'br' ? 'bl' : 'br';
    setCorner(next);
    try {
      window.localStorage.setItem(POSITION_KEY, next);
    } catch {
      // Position is a convenience; losing it is not worth surfacing.
    }
  }, [corner]);

  toggleCornerRef.current = toggleCorner;

  if (suppressed && !isPage) {
    return null;
  }

  const side = corner === 'br' ? 'right-4 md:right-6' : 'left-4 md:left-6';

  // ---- Collapsed ----------------------------------------------------------
  if (!isOpen) {
    return (
      <>
        <GlassRefractionFilter />
        {nudgeVisible && (
          <button
            type="button"
            onClick={openPanel}
            className={`iv-glass iv-sheen iv-rise-in fixed bottom-20 ${side} z-40 max-w-[min(280px,calc(100vw-2rem))] px-4 py-3 text-left`}
          >
            <div className="iv-glass-refract-layer" aria-hidden />
            <p className="relative text-sm leading-snug text-[var(--brand-light)]">
              {greetingFor()}
            </p>
            <p className="relative mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
              Tap to reply
            </p>
          </button>
        )}
        <button
          type="button"
          onClick={openPanel}
          aria-label="Open the concierge"
          className={`iv-glass iv-sheen fixed bottom-5 ${side} z-40 flex items-center gap-2 px-4 py-3 transition-all duration-300 hover:border-[var(--brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand)] ${
            isYielding ? 'scale-90 opacity-30' : 'scale-100 opacity-100'
          }`}
          style={{ borderRadius: '9999px' }}
        >
          <div className="iv-glass-refract-layer" aria-hidden />
          <MessageCircle className="relative h-4 w-4 text-[var(--brand)]" />
          <span className="relative text-sm text-[var(--brand)]">Ask us</span>
        </button>
      </>
    );
  }

  // ---- Open ---------------------------------------------------------------
  return (
    <>
      <GlassRefractionFilter />
      <div
        ref={panelRef}
        className={
          isPage
            ? 'iv-glass relative flex h-full w-full flex-col'
            : `iv-glass iv-sheen iv-rise-in fixed z-40 flex flex-col ${dragPos ? '' : `bottom-5 ${side}`}`
        }
        style={
          isPage
            ? undefined
            : {
              width: `min(${panelSize.width}px, calc(100vw - 2rem))`,
              height: `min(${panelSize.height}px, calc(100vh - 2rem))`,
              ...(dragPos ? { left: dragPos.x, top: dragPos.y } : {}),
            }
        }
        role={isPage ? 'region' : 'dialog'}
        aria-label={isPage ? 'Collector conversation' : 'Concierge'}
      >
        {/*
          Eight resize zones around the whole border — four edges, four
          corners. A single corner handle was a fiddly target and left the other
          three sides inert, which read as "resize is broken" rather than
          "resize is only there".

          They sit OUTSIDE the panel's padding as thin strips so they never
          steal a click from the transcript or the composer, and they are
          aria-hidden and unfocusable: this is a pointer affordance, and the
          keyboard path is the Reset control in the header.

          The top strips DO overlap the header bar, which is why Reset and Close
          carry `relative z-30` and the header deliberately sets no z-index — a
          z-index there would make a stacking context and trap those buttons
          underneath, turning a press on Close into a resize.
        */}
        {!isPage && RESIZE_ZONES.map(({ edge, className }) => (
          <div
            key={edge}
            aria-hidden
            onPointerDown={startResize(edge)}
            style={{ cursor: EDGE_CURSOR[edge] }}
            className={`absolute z-20 touch-none ${className}`}
          />
        ))}

        {/*
          The WHOLE header bar drags, not just the grip. One small icon was a
          fiddly target; a title bar is where everyone already expects to grab
          a panel. Buttons inside it are excluded in the handler rather than by
          stacking, so Reset and Close keep working.
        */}
        <div
          onPointerDown={isPage ? undefined : onHeaderPointerDown}
          className={`relative flex items-center justify-between border-b border-[hsla(39,47%,60%,0.16)] px-3 py-2 ${
            isPage ? '' : 'cursor-grab touch-none active:cursor-grabbing'
          }`}
        >
          <div className="flex items-center gap-1.5">
            {/* Decorative: it signals the bar is draggable. The bar handles it. */}
            {!isPage && <GripVertical aria-hidden className="h-4 w-4 text-muted-foreground" />}
            <span className="font-display text-sm tracking-wide text-[var(--brand)]">
              {isInterview ? 'Collector conversation' : 'Concierge'}
            </span>
            {isWorking && <WorkingDots />}
          </div>
          {!isPage && (dragPos || panelSize.width !== DEFAULT_PANEL.width || panelSize.height !== DEFAULT_PANEL.height) && (
            <button
              type="button"
              onClick={resetPanel}
              aria-label="Reset the panel size and position"
              title="Reset size and position"
              className="relative z-30 rounded px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground transition-colors hover:text-[var(--brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand)]"
            >
              Reset
            </button>
          )}
          {isPage ? (
            <a
              href="/"
              className="relative z-30 rounded px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground transition-colors hover:text-[var(--brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand)]"
            >
              Back to the site
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close the concierge"
              className="relative z-30 rounded p-1 text-muted-foreground transition-colors hover:text-[var(--brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand)]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div ref={scrollRef} className="relative flex-1 overflow-y-auto px-3 py-3">
          {messages.map((message) => (
            <Bubble key={message.id} message={message} />
          ))}
          {thread === undefined && (
            <p className="text-sm text-muted-foreground">One moment…</p>
          )}
        </div>

        {/* The consent moment. The model asked for it; the collector decides
            it. The tap calls a mutation the model cannot reach, and the
            sentence shown here is stored verbatim with the consent. */}
        {pendingConsentEmail && (
          <div
            className="relative mx-3 mb-2 rounded-xl border border-[hsla(39,47%,60%,0.3)] bg-[hsla(39,47%,60%,0.08)] px-3 py-3"
            data-testid="conversation-consent"
          >
            <p className="whitespace-pre-line text-xs leading-snug text-foreground">{consentText}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Reward goes to <span className="text-[var(--brand-light)]">{pendingConsentEmail}</span>.
              Wrong address? Just say so.
            </p>
            <button
              type="button"
              onClick={() => void agree()}
              disabled={consentBusy || isWorking || needsHumanCheck}
              className="mt-2 w-full rounded-lg bg-[var(--brand)] px-3 py-2 text-sm font-semibold text-[var(--vault-black)] transition-colors hover:bg-[var(--brand-light)] disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand)]"
            >
              {consentBusy ? 'One moment…' : 'I agree'}
            </button>
          </div>
        )}

        {sendError && (
          <p className="relative px-3 pb-2 text-xs leading-snug text-[var(--brand-light)]">
            {sendError}
          </p>
        )}

        {needsHumanCheck && canRunHumanCheck && <HumanCheck onVerified={handleVerified} />}

        {humanCheckUnavailable && (
          <p className="relative px-3 pb-2 text-xs leading-snug text-muted-foreground">
            I can’t verify this browser right now, so I’m not able to chat — that
            is on us, not you. The contact page reaches a person directly.
          </p>
        )}

        <form
          className="relative flex items-center gap-2 border-t border-[hsla(39,47%,60%,0.16)] px-3 py-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <input
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={needsHumanCheck ? 'One moment…' : isInterview ? 'Your answer…' : 'Ask me anything…'}
            disabled={needsHumanCheck}
            aria-label="Message the concierge"
            maxLength={MAX_MESSAGE_LENGTH}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <button
            type="submit"
            disabled={draft.trim().length === 0 || isWorking || needsHumanCheck}
            aria-label="Send"
            className="rounded-full p-1.5 text-[var(--brand)] transition-opacity disabled:opacity-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand)]"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </>
  );
}

/** The "it is working" tell. Deliberately in the header, where it cannot be scrolled away from. */
function WorkingDots(): JSX.Element {
  return (
    <span className="ml-1 flex items-center gap-[3px]" aria-label="The concierge is thinking">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="iv-think-dot inline-block h-[4px] w-[4px] rounded-full bg-[var(--brand)]"
          style={{ animationDelay: `${index * 0.16}s` }}
        />
      ))}
    </span>
  );
}

interface BubbleMessage {
  id: string;
  role: string;
  text: string;
  isComplete: boolean;
  toolCalls: Array<{
    name: string;
    label: string;
    status: string;
    productSlug?: string;
    ticketRef?: string;
  }>;
  errorText?: string;
}

function Bubble({ message }: { message: BubbleMessage }): JSX.Element {
  const [, setLocation] = useLocation();
  const mine = message.role === 'user';

  return (
    <div className={`mb-3 flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[86%] ${mine ? 'text-right' : 'text-left'}`}>
        {/* Tool chips sit ABOVE the text, because that is the order they
            happened in — the agent looks something up, then answers. */}
        {message.toolCalls.map((call, index) => (
          <div
            key={`${call.name}-${index}`}
            className="mb-1.5 inline-flex items-center gap-1.5 rounded-full border border-[hsla(39,47%,60%,0.24)] bg-[hsla(39,47%,60%,0.07)] px-2.5 py-1 text-[11px] text-[var(--brand-light)]"
          >
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${
                call.status === 'running'
                  ? 'iv-think-dot bg-[var(--brand)]'
                  : call.status === 'error'
                    ? 'bg-destructive'
                    : 'bg-[var(--brand)]'
              }`}
            />
            {call.label}
          </div>
        ))}

        {message.text.length > 0 && (
          <p
            className={`whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm leading-relaxed ${
              mine
                ? 'bg-[hsla(39,47%,60%,0.14)] text-foreground'
                : 'bg-[hsla(240,6%,100%,0.05)] text-foreground'
            }`}
          >
            {message.text}
            {!message.isComplete && <Caret />}
          </p>
        )}

        {/* A product the agent pulled up. Rendered as a link rather than a
            navigation, so it never yanks the page out from under someone
            mid-sentence. */}
        {message.toolCalls
          .filter((call) => call.productSlug)
          .map((call) => (
            <button
              key={call.productSlug}
              type="button"
              onClick={() => setLocation(`/products/${call.productSlug}`)}
              className="mt-1.5 inline-flex items-center gap-1 rounded-lg border border-[hsla(39,47%,60%,0.3)] px-3 py-1.5 text-xs text-[var(--brand)] transition-colors hover:bg-[hsla(39,47%,60%,0.1)]"
            >
              Open the product page
              <ArrowUpRight className="h-3 w-3" />
            </button>
          ))}

        {/*
          A failed turn must not look like silence.

          This was `text-xs text-muted-foreground` — grey micro-copy under an
          empty bubble. When the history window bug settled three turns in a row
          with "I lost the thread there", the visitor reported it as the widget
          simply never replying; the explanation was on screen and did not read
          as one. Give it the same weight as a message and an icon to break the
          scan, so "something went wrong" is legible at a glance.
        */}
        {message.errorText && (
          <p
            role="status"
            className="mt-1 inline-flex items-start gap-1.5 rounded-lg border border-[hsla(39,47%,60%,0.22)] bg-[hsla(39,47%,60%,0.07)] px-3 py-2 text-sm leading-snug text-[var(--brand-light)]"
          >
            <AlertCircle aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {message.errorText}
          </p>
        )}
      </div>
    </div>
  );
}

/** Blinking caret while a reply is still being written. */
function Caret(): JSX.Element {
  return (
    <span className="iv-think-dot ml-0.5 inline-block h-[0.9em] w-[2px] translate-y-[2px] bg-[var(--brand)]" />
  );
}
